import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import ContactLogPanel from './ContactLogPanel';
import {
  activityLine, activityTip, dueCell, lastContactCell, latestNoteCell, metricInfo, ngrCell, playerName, queueRanks, tierInfo,
} from './queueView';

// Pill tones: text, tint and border per semantic tone.
const TONES = {
  positive: 'border-positive/30 bg-positive/10 text-positive',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  negative: 'border-negative/40 bg-negative/15 text-negative-light',
  info: 'border-brand-blue/40 bg-brand-blue/10 text-brand-blue',
  neutral: 'border-white/15 bg-surface-2 text-ink',
  muted: 'border-white/10 bg-surface-2 text-ink-dim',
};

const COLUMNS = 'grid grid-cols-[36px_minmax(200px,1.2fr)_minmax(190px,0.9fr)_90px_120px_minmax(200px,1.3fr)_110px_88px] items-center gap-4 px-5';

function Pill({ tone, round, children }) {
  return (
    <span className={cn('inline-flex items-center whitespace-nowrap border text-xs font-bold',
      round ? 'h-[26px] rounded-full px-2.5' : 'h-6 rounded-md px-2', TONES[tone] || TONES.muted)}
    >
      {children}
    </span>
  );
}

function Header({ metric, sort }) {
  return (
    <div className={cn(COLUMNS, 'py-3 text-xs font-bold tracking-wide text-ink-dim')}>
      <div>#</div>
      <div>Player</div>
      <div>Activity <span className="font-medium">· by {metricInfo(metric).label.toLowerCase()}</span></div>
      <div className="text-right">{sort === 'ngr_total' ? 'NGR total' : 'NGR 30d'}</div>
      <div>Last contact</div>
      <div>Latest note</div>
      <div>Due</div>
      <div><span className="sr-only">Contact</span></div>
    </div>
  );
}

function Row({ row, rank, metric, sort, open, onLog }) {
  const tier = tierInfo(row.tier);
  const ngr = ngrCell(row, sort);
  const lc = lastContactCell(row);
  const note = latestNoteCell(row);
  const due = dueCell(row);
  const name = playerName(row);
  return (
    <div className={cn(COLUMNS, 'py-3.5 transition-colors hover:bg-surface-1')}>
      <div className={cn('text-[13px] font-bold tabular-nums', row.contact_state === 'due' ? 'text-negative-light' : 'text-ink-dim')}>
        {rank ?? '–'}
      </div>
      <div className="min-w-0">
        <Link
          to={`/player/${row.user_id}?from=tracker`} title="Open the player profile"
          className="block truncate text-[15px] font-bold text-ink hover:text-cta hover:underline"
        >
          {name}
        </Link>
        <div className="mt-0.5 truncate text-[12.5px] text-ink-dim">{row.user_id}{row.email ? ` · ${row.email}` : ''}</div>
      </div>
      <div className="flex min-w-0 items-center gap-2.5" title={activityTip(row)}>
        <Pill tone={tier.tone}>{tier.pill}</Pill>
        <span className="truncate text-[13px] text-ink-dim">{activityLine(row, metric)}</span>
      </div>
      <div className="text-right tabular-nums">
        <div className="text-[15px] font-bold text-ink">{ngr.main}</div>
        <div className="mt-0.5 text-xs text-ink-dim">{ngr.sub}</div>
      </div>
      <div className="min-w-0">
        <div className={cn('text-[13px]', row.last_contact ? 'text-ink' : 'text-ink-dim')}>{lc.top}</div>
        <div className="mt-0.5 truncate text-xs text-ink-dim">{lc.sub}</div>
      </div>
      <div className="min-w-0" title={note?.text}>
        <div className={cn('line-clamp-2 text-[13px] leading-snug', note ? 'text-ink' : 'text-ink-mute')}>{note ? note.text : 'No notes yet'}</div>
        {note && <div className="mt-0.5 truncate text-xs text-ink-dim">{note.meta}</div>}
      </div>
      <div>
        <div className="inline-flex flex-col items-center">
          <Pill tone={due.tone} round>{due.label}</Pill>
          <div className="mt-1 whitespace-nowrap text-xs text-ink-dim">{due.sub}</div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          type="button" variant="outline" aria-label={`Log contact for ${name}`} aria-expanded={open}
          className={cn('h-11 min-w-[76px] border-cta text-cta hover:bg-cta/10 hover:text-cta', open && 'bg-cta/10')}
          onClick={() => onLog(open ? null : row.user_id)}
        >
          Log
        </Button>
      </div>
    </div>
  );
}

// QueueTable: the ranked rows; the open row expands into the log panel. Due players are ranked
// across pages (rankOffset counts the rows before this page; due players always come first).
export default function QueueTable({ rows, rankOffset, metric, sort, openId, working, onLog, onCancel, onSaved }) {
  const ranks = queueRanks(rows, rankOffset);
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10 bg-surface">
      <div className="min-w-[1220px]">
        <Header metric={metric} sort={sort} />
        {rows.map((r, i) => {
          const open = openId === r.user_id;
          return (
            <div key={r.user_id} className={cn('border-t border-white/10', open && 'bg-surface-1/60')}>
              <Row row={r} rank={ranks[i]} metric={metric} sort={sort} open={open} onLog={onLog} />
              {open && (
                <ContactLogPanel
                  row={r} saveLabel={working ? 'Save & next player' : 'Save contact'}
                  onCancel={onCancel} onSaved={(res) => onSaved(r, res)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
